import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import Shell from "../components/Shell";
import GradeBadge from "../components/GradeBadge";
import { useAuth } from "../lib/auth";
import { api, gradeFor } from "../lib/api";
function toInput(v, absent) {
    if (absent)
        return "AB";
    if (v == null)
        return "";
    return String(v);
}
function computeLocal(p1, p2, a1, a2, usePart1, usePart2, maxMarks) {
    const P1 = usePart1 ? (a1 ? "AB" : p1) : null;
    const P2 = usePart2 ? (a2 ? "AB" : p2) : null;
    let total;
    if (P1 == null && P2 == null)
        total = null;
    else if (P1 === "AB" && P2 === "AB")
        total = "AB";
    else if (P1 === "AB" && !usePart2)
        total = "AB";
    else if (P2 === "AB" && !usePart1)
        total = "AB";
    else if (P1 === "AB")
        total = typeof P2 === "number" ? P2 : "AB";
    else if (P2 === "AB")
        total = typeof P1 === "number" ? P1 : "AB";
    else if (typeof P1 === "number" && typeof P2 === "number")
        total = P1 + P2;
    else if (typeof P1 === "number" && !usePart2)
        total = P1;
    else if (typeof P2 === "number" && !usePart1)
        total = P2;
    else
        total = null;
    return { total, grade: gradeFor(total, maxMarks) };
}
function parseInput(raw) {
    const s = raw.trim().toUpperCase();
    if (s === "")
        return { val: null, absent: 0 };
    if (s === "AB")
        return { val: null, absent: 1 };
    // Half-typed AB — user hasn't finished, don't error, don't save yet.
    if (s === "A")
        return { val: null, absent: 0, partial: true };
    const n = parseFloat(s);
    if (!Number.isFinite(n))
        return { val: null, absent: 0, err: `"${raw}" isn't a number or AB` };
    return { val: n, absent: 0 };
}
export default function EntryGrid() {
    const { examId } = useParams();
    const { user } = useAuth();
    const backTo = user?.role === "admin" ? "/admin/marks" : "/teacher";
    const [exam, setExam] = useState(null);
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const inputRefs = useRef([]);
    useEffect(() => {
        if (!examId)
            return;
        (async () => {
            setLoading(true);
            const { exam, rows } = await api.get(`/api/exams/${examId}/scores`);
            setExam(exam);
            setRows(rows.map((r) => ({
                ...r,
                _p1_input: toInput(r.part1, r.part1_absent),
                _p2_input: toInput(r.part2, r.part2_absent),
                _status: "idle",
            })));
            setLoading(false);
        })();
    }, [examId]);
    const p1Max = exam?.part1_max ?? 0;
    const p2Max = exam?.part2_max ?? 0;
    const usePart1 = p1Max > 0;
    const usePart2 = p2Max > 0;
    const singlePaper = !(usePart1 && usePart2);
    const totalMax = p1Max + p2Max;
    const stats = useMemo(() => {
        let entered = 0, absent = 0, sum = 0, count = 0;
        for (const r of rows) {
            const has1 = usePart1 && r._p1_input !== "";
            const has2 = usePart2 && r._p2_input !== "";
            if (has1 || has2)
                entered++;
            if (r.total === "AB")
                absent++;
            if (typeof r.total === "number") {
                sum += r.total;
                count++;
            }
        }
        return { entered, absent, avg: count ? sum / count : null, count };
    }, [rows, usePart1, usePart2]);
    const save = async (idx) => {
        const r = rows[idx];
        const p1p = usePart1
            ? parseInput(r._p1_input)
            : { val: null, absent: 0, partial: false, err: undefined };
        const p2p = usePart2
            ? parseInput(r._p2_input)
            : { val: null, absent: 0, partial: false, err: undefined };
        // Half-typed input ("A" on its way to "AB") — leave it alone, don't save.
        if (p1p.partial || p2p.partial)
            return;
        if (p1p.err || p2p.err) {
            updateRow(idx, { _status: "error", _error: p1p.err ?? p2p.err });
            return;
        }
        if (usePart1 && p1p.val !== null && (p1p.val < 0 || p1p.val > p1Max)) {
            updateRow(idx, { _status: "error", _error: `Value must be 0–${p1Max}` });
            return;
        }
        if (usePart2 && p2p.val !== null && (p2p.val < 0 || p2p.val > p2Max)) {
            updateRow(idx, { _status: "error", _error: `Part 2 must be 0–${p2Max}` });
            return;
        }
        updateRow(idx, { _status: "saving", _error: undefined });
        try {
            await api.patch(`/api/scores/${examId}/${r.student_id}`, {
                part1: usePart1 ? (p1p.absent ? "AB" : p1p.val) : null,
                part2: usePart2 ? (p2p.absent ? "AB" : p2p.val) : null,
            });
            updateRow(idx, {
                part1: p1p.val,
                part2: p2p.val,
                part1_absent: p1p.absent,
                part2_absent: p2p.absent,
                _status: "saved",
            });
            setTimeout(() => setRows((rs) => rs.map((x, i) => i === idx ? { ...x, _status: "idle" } : x)), 900);
        }
        catch (e) {
            updateRow(idx, { _status: "error", _error: e.message ?? "Save failed" });
        }
    };
    const updateRow = (idx, patch) => {
        setRows((rs) => rs.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
    };
    const onChange = (idx, part, raw) => {
        setRows((rs) => rs.map((r, i) => {
            if (i !== idx)
                return r;
            const next = { ...r };
            if (part === "p1")
                next._p1_input = raw;
            else
                next._p2_input = raw;
            const p1p = usePart1 ? parseInput(next._p1_input) : { val: null, absent: 0 };
            const p2p = usePart2 ? parseInput(next._p2_input) : { val: null, absent: 0 };
            const local = computeLocal(p1p.val, p2p.val, p1p.absent, p2p.absent, usePart1, usePart2, totalMax);
            next.total = local.total;
            next.grade = local.grade;
            next._status = "idle";
            next._error = undefined;
            return next;
        }));
    };
    const onKeyDown = (e, ri, ci) => {
        if (e.key === "Enter") {
            e.preventDefault();
            const next = inputRefs.current[ri + 1]?.[ci];
            if (next)
                next.focus();
        }
        else if (e.key === "Tab" && !e.shiftKey) {
            // On the last input in this row, jump straight to the next row's first input.
            const lastInputIndex = singlePaper ? 0 : 1;
            if (ci === lastInputIndex && ri < rows.length - 1) {
                e.preventDefault();
                inputRefs.current[ri + 1]?.[0]?.focus();
            }
        }
    };
    const setRef = (ri, ci) => (el) => {
        if (!inputRefs.current[ri])
            inputRefs.current[ri] = [null, null];
        inputRefs.current[ri][ci] = el;
    };
    return (_jsx(Shell, { right: exam && (_jsxs("div", { className: "hidden md:flex items-center gap-4 text-xs text-slate2", children: [_jsxs("span", { children: ["Entered ", _jsx("b", { className: "text-ink font-mono", children: stats.entered }), " / ", rows.length] }), _jsxs("span", { children: ["Absent ", _jsx("b", { className: "text-ink font-mono", children: stats.absent })] }), stats.avg != null && _jsxs("span", { children: ["Avg ", _jsx("b", { className: "text-ink font-mono", children: stats.avg.toFixed(1) }), "/", totalMax] })] })), children: loading || !exam ? (_jsx("p", { className: "text-slate2 text-sm", children: "Loading\u2026" })) : (_jsxs(_Fragment, { children: [_jsxs("div", { className: "flex items-center gap-3 mb-5 flex-wrap", children: [_jsx(Link, { to: backTo, className: "btn-ghost text-xs", children: "\u2190 Back" }), _jsxs("div", { children: [_jsxs("div", { className: "eyebrow", children: ["Grade ", exam.class_id, singlePaper && " · Single paper"] }), _jsx("h1", { className: "font-display text-2xl", children: exam.name })] }), _jsx("div", { className: "flex-1" }), _jsx("span", { className: "text-xs text-slate2", children: singlePaper
                                ? _jsxs(_Fragment, { children: ["Marks: /", totalMax] })
                                : _jsxs(_Fragment, { children: ["Part 1: /", p1Max, " \u00B7 Part 2: /", p2Max, " \u00B7 Total: /", totalMax] }) })] }), _jsx("div", { className: "card overflow-x-auto", children: _jsxs("table", { className: "w-full text-sm", children: [_jsx("thead", { children: _jsxs("tr", { className: "bg-paper text-slate2 text-[11px] uppercase tracking-wider", children: [_jsx("th", { className: "text-left px-3 py-2 w-10", children: "#" }), _jsx("th", { className: "text-left px-3 py-2", children: "Student" }), usePart1 && _jsx("th", { className: "text-center px-2 py-2 w-24", children: singlePaper ? "Marks" : "Part 1" }), usePart2 && _jsx("th", { className: "text-center px-2 py-2 w-24", children: singlePaper ? "Marks" : "Part 2" }), _jsx("th", { className: "text-center px-2 py-2 w-20", children: "Total" }), _jsx("th", { className: "text-center px-2 py-2 w-16", children: "Grade" }), _jsx("th", { className: "w-24" })] }) }), _jsx("tbody", { children: rows.map((r, ri) => (_jsxs("tr", { className: "border-t border-line-soft", children: [_jsx("td", { className: "px-3 py-1.5 text-slate2 font-mono", children: r.roll_no }), _jsxs("td", { className: "px-3 py-1.5", children: [r.full_name, r.gender && _jsx("span", { className: "ml-2 text-[10px] text-slate2", children: r.gender })] }), usePart1 && (_jsx("td", { className: "px-2 py-1.5 text-center", children: _jsx("input", { ref: setRef(ri, 0), className: "input text-center w-16 !py-1 font-mono", value: r._p1_input, onChange: (e) => onChange(ri, "p1", e.target.value), onBlur: () => save(ri), onKeyDown: (e) => onKeyDown(e, ri, 0), "aria-label": `${singlePaper ? "Marks" : "Part 1"} for ${r.full_name}` }) })), usePart2 && (_jsx("td", { className: "px-2 py-1.5 text-center", children: _jsx("input", { ref: setRef(ri, 1), className: "input text-center w-16 !py-1 font-mono", value: r._p2_input, onChange: (e) => onChange(ri, "p2", e.target.value), onBlur: () => save(ri), onKeyDown: (e) => onKeyDown(e, ri, 1), "aria-label": `${singlePaper ? "Marks" : "Part 2"} for ${r.full_name}` }) })), _jsx("td", { className: "px-2 py-1.5 text-center font-mono font-semibold", children: r.total == null ? "—" : r.total }), _jsx("td", { className: "px-2 py-1.5 text-center", children: _jsx(GradeBadge, { g: r.grade }) }), _jsxs("td", { className: "px-2 py-1.5 text-[11px] text-slate2", children: [r._status === "saving" && _jsx("span", { children: "Saving\u2026" }), r._status === "saved" && _jsx("span", { className: "text-ok", children: "Saved \u2713" }), r._status === "error" && _jsx("span", { className: "text-danger", children: r._error })] })] }, r.student_id))) })] }) }), _jsxs("p", { className: "text-xs text-slate2 mt-3", children: ["Tips: type ", _jsx("code", { className: "bg-paper-2 px-1 rounded", children: "AB" }), " in a cell for Absent \u00B7 Tab moves across \u00B7 Enter drops to the next student \u00B7 saves happen automatically when you leave the cell. Grade is percentage of max: A \u2265 75%, B \u2265 65%, C \u2265 50%, S \u2265 35%."] })] })) }));
}
